// One outline icon per kind of knowledge source, so a list that mixes a
// website, a PDF, a set of Q&A pairs and some pasted text can be scanned by
// shape. Same 24x24, 1.5-stroke style as the Integrations section icons.
//
// The same icon appears on the button that adds a source, on the source while
// it is being filled in, and on its row once it is stored, so it is one thing
// from start to finish.

import type { KnowledgeSource } from "../lib/types";

type SourceType = KnowledgeSource["type"];

export const SOURCE_LABELS: Record<SourceType, string> = {
  qa: "Q&A pairs",
  url: "Website",
  text: "Freeform text",
  file: "Document"
};

const PATHS: Record<SourceType, string> = {
  // A globe.
  url: "M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418",
  // A page with lines of text.
  file: "M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z",
  // Two speech bubbles: a question and its answer.
  qa: "M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155",
  // Ragged lines, like a paragraph.
  text: "M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12"
};

export function SourceIcon({ type, className = "h-4 w-4" }: { type: SourceType; className?: string }) {
  return (
    <svg
      data-icon={type}
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d={PATHS[type]} />
    </svg>
  );
}

// The tinted square used on a stored source's row, matching the Integrations
// section icons at a smaller size.
export function SourceBadge({ type }: { type: SourceType }) {
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
      <SourceIcon type={type} />
    </span>
  );
}
